const number = (value, fallback = 0) =>
    Number.isFinite(Number(value)) ? Number(value) : fallback;
const valueOf = (answers, id) => answers?.[id];

export const DEFAULT_LEAD_CAPTURE_FIELDS = [
    {
        id: 'name',
        type: 'text',
        label: 'Name',
        placeholder: 'Your name',
        required: false,
    },
    {
        id: 'email',
        type: 'email',
        label: 'Email',
        placeholder: 'you@example.com',
        required: true,
    },
    {
        id: 'phone',
        type: 'tel',
        label: 'Phone',
        placeholder: '(555) 123-4567',
        required: false,
    },
];

export const DEFAULT_LEAD_PHOTO_UPLOAD = {
    enabled: false,
    required: false,
    label: 'Upload project photos',
    helperText: 'Add up to 5 photos. JPG, PNG, or WebP.',
};

export function getLeadCaptureFields(config) {
    const configured = config?.leadCapture?.fields;
    const source =
        Array.isArray(configured) && configured.length
            ? configured
            : DEFAULT_LEAD_CAPTURE_FIELDS;
    const fields = source.map((field, index) => ({
        id: String(field?.id || `custom-field-${index + 1}`),
        type: ['email', 'tel', 'textarea'].includes(field?.type)
            ? field.type
            : 'text',
        label: String(field?.label || `Field ${index + 1}`),
        placeholder: String(field?.placeholder || ''),
        required: field?.id === 'email' ? true : Boolean(field?.required),
    }));

    if (!fields.some((field) => field.id === 'email')) {
        fields.unshift(structuredClone(DEFAULT_LEAD_CAPTURE_FIELDS[1]));
    }
    return fields;
}

function conditionEntries(group) {
    if (Array.isArray(group?.conditions)) return group.conditions;
    if (group?.conditions && typeof group.conditions === 'object')
        return [group.conditions];
    return [];
}

function hasServiceChoice(group, fieldId, value) {
    return conditionEntries(group).some((condition) =>
        condition.group
            ? hasServiceChoice(condition.group, fieldId, value)
            : condition.fieldId === fieldId &&
              condition.operator === 'contains' &&
              condition.value === value
    );
}

function replaceServiceChoice(group, fieldId, oldValue, newValue) {
    if (!conditionEntries(group).length) return group;
    return {
        ...group,
        conditions: conditionEntries(group).map((condition) =>
            condition.group
                ? {
                      ...condition,
                      group: replaceServiceChoice(
                          condition.group,
                          fieldId,
                          oldValue,
                          newValue
                      ),
                  }
                : condition.fieldId === fieldId &&
                  condition.operator === 'contains' &&
                  condition.value === oldValue
                ? { ...condition, value: newValue }
                : condition
        ),
    };
}

function audienceVariant(option) {
    const label = String(option?.label || '')
        .replace(/\s*[—–-]\s*\$.*$/, '')
        .trim();
    const match = label.match(/^(residential|commercial)\s+(.+)$/i);
    if (match)
        return { audience: match[1].toLowerCase(), service: match[2].trim() };
    const premiumCarpet = label.match(
        /^premium\s+(?:residential\s+)?(carpet(?:\s+cleaning)?)$/i
    );
    if (premiumCarpet)
        return { audience: 'residential', service: premiumCarpet[1].trim() };
    return null;
}

function combineExclusiveAudienceChoices(config) {
    const pair = config.fields
        .filter((field) => field.type === 'checkbox')
        .flatMap((field) => {
            const variants = (field.options || []).map((option) => ({
                option,
                ...audienceVariant(option),
            }));
            return variants
                .filter((variant) => variant.audience === 'residential')
                .flatMap((residential) =>
                    variants
                        .filter(
                            (commercial) =>
                                commercial.audience === 'commercial' &&
                                commercial.service.toLowerCase() ===
                                    residential.service.toLowerCase()
                        )
                        .map((commercial) => ({
                            selector: field,
                            residential: residential.option,
                            commercial: commercial.option,
                            service: residential.service,
                        }))
                );
        })
        .find(
            ({ selector, residential, commercial }) =>
                config.categories.some((category) =>
                    hasServiceChoice(
                        category.conditions,
                        selector.id,
                        residential.value
                    )
                ) &&
                config.categories.some((category) =>
                    hasServiceChoice(
                        category.conditions,
                        selector.id,
                        commercial.value
                    )
                )
        );
    if (!pair) return false;

    const { selector, residential, commercial, service } = pair;

    const existingIds = new Set(config.fields.map((field) => field.id));
    const baseId = `serviceVariant-${service
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')}`;
    let typeId = baseId;
    let suffix = 2;
    while (existingIds.has(typeId)) typeId = `${baseId}-${suffix++}`;

    const branchVisibility = (group) => {
        const isResidential = hasServiceChoice(
            group,
            selector.id,
            residential.value
        );
        const isCommercial = hasServiceChoice(
            group,
            selector.id,
            commercial.value
        );
        if (!isResidential && !isCommercial) return group;
        let updated = replaceServiceChoice(
            group,
            selector.id,
            commercial.value,
            residential.value
        );
        if (isResidential !== isCommercial) {
            updated = {
                logic: 'and',
                conditions: [
                    { group: updated },
                    {
                        fieldId: typeId,
                        operator: 'equals',
                        value: isResidential ? 'residential' : 'commercial',
                    },
                ],
            };
        }
        return updated;
    };

    config.fields.forEach((field) => {
        if (field.visibility)
            field.visibility = branchVisibility(field.visibility);
    });
    config.categories.forEach((category) => {
        if (category.conditions)
            category.conditions = branchVisibility(category.conditions);
    });
    (config.rules || []).forEach((rule) => {
        if (rule.conditions)
            rule.conditions = branchVisibility(rule.conditions);
    });
    config.customQuoteConditions = (config.customQuoteConditions || []).map(
        branchVisibility
    );

    selector.options = selector.options.filter(
        (option) => option.value !== commercial.value
    );
    residential.label = service[0].toUpperCase() + service.slice(1);
    if (Array.isArray(selector.defaultValue))
        selector.defaultValue = [
            ...new Set(
                selector.defaultValue.map((value) =>
                    value === commercial.value ? residential.value : value
                )
            ),
        ];
    config.fields.splice(config.fields.indexOf(selector) + 1, 0, {
        id: typeId,
        type: 'select',
        label: `Residential or commercial ${service.toLowerCase()}?`,
        required: true,
        options: [
            { value: 'residential', label: 'Residential' },
            { value: 'commercial', label: 'Commercial' },
        ],
        defaultValue: '',
        visibility: {
            logic: 'and',
            conditions: [
                {
                    fieldId: selector.id,
                    operator: 'contains',
                    value: residential.value,
                },
            ],
        },
    });
    return true;
}

function expandLegacyUpholsteryItems(config) {
    for (const category of config.categories) {
        if (!/upholstery/i.test(`${category.id || ''} ${category.name || ''}`))
            continue;
        if (!category.quantityFieldId || category.items?.length !== 1) continue;

        const item = category.items[0];
        const selector = config.fields.find(
            (field) => field.id === item.fieldId
        );
        const sharedQuantity = config.fields.find(
            (field) => field.id === category.quantityFieldId
        );
        if (
            selector?.type !== 'select' ||
            !Array.isArray(selector.options) ||
            selector.options.length < 2 ||
            !Array.isArray(item.options) ||
            !sharedQuantity ||
            !['quantity', 'number'].includes(sharedQuantity.type)
        )
            continue;

        const usedElsewhere =
            config.categories.some(
                (other) =>
                    other !== category &&
                    (other.quantityFieldId === sharedQuantity.id ||
                        (other.items || []).some(
                            (otherItem) =>
                                otherItem.fieldId === sharedQuantity.id
                        ))
            ) ||
            config.fields.some(
                (field) =>
                    field !== sharedQuantity &&
                    conditionFieldIds(field.visibility).includes(
                        sharedQuantity.id
                    )
            ) ||
            (config.rules || []).some(
                (rule) =>
                    conditionFieldIds(rule.conditions).includes(
                        sharedQuantity.id
                    ) ||
                    (rule.actions || []).some(
                        (action) => action.fieldId === sharedQuantity.id
                    )
            ) ||
            (config.customQuoteConditions || []).some((condition) =>
                conditionFieldIds(condition).includes(sharedQuantity.id)
            );
        if (usedElsewhere) continue;

        const prices = new Map(
            item.options.map((option) => [option.value, option])
        );
        if (selector.options.some((option) => !prices.has(option.value)))
            continue;

        selector.type = 'quantity-list';
        selector.label = selector.label.replace(
            /What (.+) item should be cleaned\?/i,
            'How many $1 items need cleaning?'
        );
        selector.defaultValue = {};
        config.fields = config.fields.filter(
            (field) => field.id !== sharedQuantity.id
        );
        category.items = [
            {
                fieldId: selector.id,
                options: selector.options.map((option) => ({
                    ...prices.get(option.value),
                    pricingType: 'per-unit',
                })),
            },
        ];
        delete category.quantityFieldId;
    }
}

function linkUnmappedPricingItems(config) {
    const fields = config.fields || [];
    const numericTypes = new Set([
        'quantity',
        'number',
        'square-footage',
        'linear-footage',
        'distance',
        'rooms',
        'hours',
        'currency',
    ]);
    for (const category of config.categories || []) {
        for (const item of category.items || []) {
            if (item.fieldId) continue;
            if (item.options?.length) {
                const values = new Set(
                    item.options.map((option) => option.value)
                );
                const candidates = fields.filter(
                    (field) =>
                        Array.isArray(field.options) &&
                        [...values].every((value) =>
                            field.options.some(
                                (option) => option.value === value
                            )
                        )
                );
                const preferred = candidates.find(
                    (field) => field.id === category.quantityFieldId
                );
                const match =
                    preferred || (candidates.length === 1 && candidates[0]);
                if (match) item.fieldId = match.id;
                continue;
            }
            const quantityId = item.quantityFieldId || category.quantityFieldId;
            const quantityField = fields.find(
                (field) => field.id === quantityId
            );
            if (numericTypes.has(quantityField?.type))
                item.fieldId = quantityId;
        }
    }
}

function repairDuplicateCheckboxSelector(config) {
    const convertedIds = new Set();
    for (const field of [...config.fields]) {
        if (
            field.type !== 'select' ||
            !field.id ||
            !Array.isArray(field.options) ||
            field.options.length < 2
        )
            continue;
        const duplicate = config.fields.find(
            (candidate) =>
                candidate !== field &&
                candidate.id === field.id &&
                candidate.type === 'checkbox' &&
                !candidate.options?.length
        );
        if (!duplicate) continue;
        const optionValues = new Set(
            field.options.map((option) => option.value)
        );
        const hasPricedOptions = config.categories.some((category) =>
            (category.items || []).some(
                (item) =>
                    item.fieldId === field.id &&
                    item.options?.length > 0 &&
                    item.options.every((option) =>
                        optionValues.has(option.value)
                    )
            )
        );
        if (!hasPricedOptions) continue;
        field.type = 'checkbox';
        field.defaultValue = [];
        field.label = duplicate.label || field.label;
        config.fields.splice(config.fields.indexOf(duplicate), 1);
        convertedIds.add(field.id);
    }
    if (!convertedIds.size) return;
    const updateConditions = (group) => {
        for (const condition of conditionEntries(group)) {
            if (condition.group) updateConditions(condition.group);
            else if (convertedIds.has(condition.fieldId)) {
                if (condition.operator === 'equals')
                    condition.operator = 'contains';
                else if (condition.operator === 'does-not-equal')
                    condition.operator = 'not-contains';
            }
        }
    };
    config.fields.forEach((field) => updateConditions(field.visibility));
    config.categories.forEach((category) => {
        updateConditions(category.conditions);
        (category.items || []).forEach((item) =>
            updateConditions(item.conditions)
        );
    });
    (config.rules || []).forEach((rule) => updateConditions(rule.conditions));
    (config.customQuoteConditions || []).forEach(updateConditions);
    for (const fieldId of convertedIds) {
        const selector = config.fields.find((field) => field.id === fieldId);
        const labels = new Map(
            selector.options.map((option) => [
                option.value,
                option.label || option.value,
            ])
        );
        const addedFields = [];
        for (const category of config.categories) {
            category.items = (category.items || []).flatMap((item) => {
                if (
                    item.fieldId !== fieldId ||
                    !item.options?.some(
                        (option) => option.pricingType === 'per-unit'
                    ) ||
                    item.quantityFieldId ||
                    category.quantityFieldId
                )
                    return [item];
                return item.options.map((option) => {
                    const selection = {
                        logic: 'and',
                        conditions: [
                            {
                                fieldId,
                                operator: 'contains',
                                value: option.value,
                            },
                        ],
                    };
                    const conditions = item.conditions
                        ? {
                              logic: 'and',
                              conditions: [
                                  { group: item.conditions },
                                  { group: selection },
                              ],
                          }
                        : selection;
                    const optionLabel =
                        labels.get(option.value) || option.value;
                    const label = optionLabel
                        .replace(/\s*[—–-]\s*\$.*$/, '')
                        .trim();
                    const result = {
                        id: `${item.id || fieldId}_${option.value}`,
                        name: label,
                        price: option.price,
                        pricingType: option.pricingType || 'fixed',
                        conditions,
                    };
                    if (option.pricingType === 'per-unit') {
                        const quantityId = `${fieldId}_${String(option.value)
                            .toLowerCase()
                            .replace(/[^a-z0-9]+/g, '_')}_quantity`;
                        result.fieldId = quantityId;
                        if (
                            !config.fields.some(
                                (field) => field.id === quantityId
                            )
                        ) {
                            const isArea =
                                /sq\.?\s*ft\.?|square\s*(?:feet|foot)/i.test(
                                    optionLabel
                                );
                            addedFields.push({
                                id: quantityId,
                                type: 'number',
                                label: isArea
                                    ? `How many square feet for ${label}?`
                                    : `How many for ${label}?`,
                                min: 1,
                                step: 1,
                                required: true,
                                visibility: selector.visibility
                                    ? {
                                          logic: 'and',
                                          conditions: [
                                              { group: selector.visibility },
                                              { group: selection },
                                          ],
                                      }
                                    : selection,
                            });
                        }
                        const minimum = optionLabel.match(
                            /\$([\d,]+(?:\.\d+)?)\s*minimum/i
                        );
                        if (minimum)
                            result.minimumPrice = Number(
                                minimum[1].replace(/,/g, '')
                            );
                    }
                    return result;
                });
            });
        }
        if (addedFields.length) {
            const index = config.fields.indexOf(selector);
            config.fields.splice(index + 1, 0, ...addedFields);
        }
    }
}

/**
 * Older AI-generated estimates sometimes create several services which all use
 * the same measurement field, but omit a way for the visitor to choose those
 * services. Add that missing choice without changing the editor's saved data.
 */
export function normalizeCalculatorConfig(config) {
    if (!config?.fields || !Array.isArray(config?.categories)) return config;

    const normalized = structuredClone(config);
    repairDuplicateCheckboxSelector(normalized);
    while (combineExclusiveAudienceChoices(normalized)) {
        // A company can have more than one residential/commercial service pair.
    }
    expandLegacyUpholsteryItems(normalized);
    linkUnmappedPricingItems(normalized);
    const unconditional = normalized.categories.filter(
        (category) => !category.conditions
    );
    const fieldCounts = new Map();

    unconditional.forEach((category) => {
        const seen = new Set();
        (category.items || []).forEach((item) => {
            if (item?.fieldId && !item?.options?.length) seen.add(item.fieldId);
        });
        seen.forEach((fieldId) =>
            fieldCounts.set(fieldId, (fieldCounts.get(fieldId) || 0) + 1)
        );
    });

    const [sharedFieldId, sharedFieldCount] =
        [...fieldCounts.entries()].sort((a, b) => b[1] - a[1])[0] || [];
    if (!sharedFieldId || sharedFieldCount < 2) return normalized;

    const selectableCategories = unconditional.filter((category) =>
        (category.items || []).some(
            (item) => item?.fieldId === sharedFieldId && !item?.options?.length
        )
    );
    if (selectableCategories.length < 2) return normalized;

    const existingIds = new Set(normalized.fields.map((field) => field.id));
    let selectorId = 'selectedServices';
    let suffix = 2;
    while (existingIds.has(selectorId)) {
        selectorId = `selectedServices${suffix}`;
        suffix += 1;
    }

    const selector = {
        id: selectorId,
        type: 'checkbox',
        label: 'Which services do you need?',
        options: selectableCategories.map((category) => ({
            value: category.id,
            label: category.name || category.id,
        })),
        defaultValue: selectableCategories.map((category) => category.id),
    };
    const measurementIndex = normalized.fields.findIndex(
        (field) => field.id === sharedFieldId
    );
    normalized.fields.splice(Math.max(measurementIndex, 0), 0, selector);
    selectableCategories.forEach((category) => {
        category.conditions = {
            logic: 'and',
            conditions: [
                {
                    fieldId: selectorId,
                    operator: 'contains',
                    value: category.id,
                },
            ],
        };
    });
    return normalized;
}

export function evaluateCondition(group, answers = {}) {
    if (!group) return true;
    const results = conditionEntries(group).map((condition) => {
        if (condition.group) return evaluateCondition(condition.group, answers);
        if (!condition.fieldId || !condition.operator) return false;
        const left = valueOf(answers, condition.fieldId);
        const right = condition.value;
        switch (condition.operator) {
            case 'contains':
                return Array.isArray(left)
                    ? left.includes(right)
                    : String(left ?? '').includes(String(right));
            case 'not-contains':
                return Array.isArray(left)
                    ? !left.includes(right)
                    : !String(left ?? '').includes(String(right));
            case 'is-selected':
                return Array.isArray(left) && left.includes(right);
            case 'has-value':
                return left !== undefined && left !== null && left !== '';
            case 'is-empty':
                return left === undefined || left === null || left === '';
            case 'greater-than':
                return number(left) > number(right);
            case 'less-than':
                return number(left) < number(right);
            case 'greater-than-or-equal':
                return number(left) >= number(right);
            case 'less-than-or-equal':
                return number(left) <= number(right);
            case 'does-not-equal':
                return left !== right;
            default:
                return left === right;
        }
    });
    if (!results.length) return true;
    return group.logic === 'or'
        ? results.some(Boolean)
        : results.every(Boolean);
}

const round = (value) =>
    Math.round((number(value) + Number.EPSILON) * 100) / 100;
const optionPrice = (option, quantity) =>
    option?.pricingType === 'per-unit'
        ? number(option.price) * quantity
        : number(option?.price);

function categorySubtotal(category, answers, visibleFieldIds) {
    let total = number(category.basePrice);
    for (const item of category.items || []) {
        if (item.conditions && !evaluateCondition(item.conditions, answers))
            continue;
        if (item.fieldId && !visibleFieldIds.has(item.fieldId)) continue;
        const quantityFieldId =
            item.quantityFieldId || category.quantityFieldId;
        const quantity = number(answers?.[quantityFieldId], 1);
        const selected = answers?.[item.fieldId];
        if (!item.fieldId && !item.options?.length) {
            if (item.conditions) total += number(item.price);
            continue;
        }
        if (
            selected &&
            typeof selected === 'object' &&
            !Array.isArray(selected)
        ) {
            for (const [value, rawQuantity] of Object.entries(selected)) {
                const itemQuantity = number(rawQuantity);
                if (itemQuantity <= 0) continue;
                const option = (item.options || []).find(
                    (candidate) => candidate.value === value
                );
                if (option) total += number(option.price) * itemQuantity;
            }
            continue;
        }
        const values = Array.isArray(selected) ? selected : [selected];
        for (const choice of values) {
            if (choice === undefined || choice === '' || choice === false)
                continue;
            const option = (item.options || []).find(
                (candidate) => candidate.value === choice
            );
            const itemQuantity = category.quantityFieldId
                ? quantity
                : number(choice, 1);
            if (item.pricingType === 'per-unit' && itemQuantity <= 0) continue;
            const lineTotal = option
                ? optionPrice(option, quantity)
                : number(item.price) *
                  (item.pricingType === 'per-unit' ? itemQuantity : 1);
            total += item.minimumPrice
                ? Math.max(lineTotal, number(item.minimumPrice))
                : lineTotal;
        }
    }
    for (const tier of category.tiers || [])
        if (number(answers?.[tier.fieldId]) >= number(tier.minimum))
            total += number(tier.price);
    return round(total);
}

export function getVisibleFields(config, answers) {
    return (config?.fields || []).filter(
        (field) =>
            !field.visibility || evaluateCondition(field.visibility, answers)
    );
}

export function suggestQuestionDisplay(config) {
    const fields = config?.fields || [];
    const conditionalFields = fields.filter((field) => field.visibility).length;
    return fields.length >= 6 ||
        conditionalFields >= 3 ||
        (config?.categories || []).length >= 4
        ? 'stepper'
        : 'all';
}

export function isRequiredAnswerMissing(field, answer) {
    if (!field?.required) return false;
    if (Array.isArray(answer)) return answer.length === 0;
    if (field.type === 'quantity-list')
        return (
            !answer ||
            typeof answer !== 'object' ||
            !Object.values(answer).some((quantity) => number(quantity) > 0)
        );
    if (field.type === 'boolean') return answer !== true;
    if (answer === null || answer === undefined) return true;
    if (typeof answer === 'string') return answer.trim() === '';
    if (typeof answer === 'number')
        return (
            !Number.isFinite(answer) ||
            (field.min !== undefined && answer < Number(field.min))
        );
    return false;
}

function conditionFieldIds(group) {
    return conditionEntries(group).flatMap((condition) => {
        if (condition?.group) return conditionFieldIds(condition.group);
        return condition?.fieldId ? [condition.fieldId] : [];
    });
}

function conditionLeaves(group) {
    return conditionEntries(group).flatMap((condition) =>
        condition?.group ? conditionLeaves(condition.group) : [condition]
    );
}

function minimumCategory(rule, categories) {
    const requested = conditionLeaves(rule.conditions);
    if (!requested.length) return null;
    const matches = categories
        .map((category) => {
            const categoryLeaves = conditionLeaves(category.conditions);
            const itemLeaves = (category.items || []).flatMap((item) =>
                conditionLeaves(item.conditions)
            );
            const score = requested.reduce((sum, condition) => {
                const same = (candidate) =>
                    candidate?.fieldId === condition?.fieldId &&
                    candidate?.operator === condition?.operator &&
                    candidate?.value === condition?.value;
                if (itemLeaves.some(same)) return sum + 2;
                if (
                    (category.items || []).some(
                        (item) => item.fieldId === condition?.fieldId
                    )
                )
                    return sum + 2;
                return sum + (categoryLeaves.some(same) ? 1 : 0);
            }, 0);
            return { category, score };
        })
        .sort((a, b) => b.score - a.score);
    return matches[0]?.score > 0 && matches[0].score > (matches[1]?.score || 0)
        ? matches[0].category
        : null;
}

export function validateCalculatorConfig(config) {
    const errors = [];
    const fields = config?.fields || [];
    const ids = new Set();
    fields.forEach((field) => {
        if (!field.id || ids.has(field.id))
            errors.push(
                `Duplicate or missing field ID: ${field.id || '(empty)'}`
            );
        ids.add(field.id);
    });
    fields.forEach((field, index) => {
        const earlierFieldIds = new Set(
            fields.slice(0, index).map((candidate) => candidate.id)
        );
        conditionFieldIds(field.visibility).forEach((fieldId) => {
            if (!earlierFieldIds.has(fieldId))
                errors.push(
                    `Visibility for ${
                        field.id || '(unnamed field)'
                    } must reference an earlier field`
                );
        });
    });
    const categoryIds = new Set();
    (config?.categories || []).forEach((category) => {
        if (!category.id || categoryIds.has(category.id))
            errors.push(
                `Duplicate or missing category ID: ${category.id || '(empty)'}`
            );
        categoryIds.add(category.id);
        if (category.quantityFieldId && !ids.has(category.quantityFieldId))
            errors.push(
                `Category ${category.id} references a missing quantity field`
            );
        conditionFieldIds(category.conditions).forEach((fieldId) => {
            if (!ids.has(fieldId))
                errors.push(
                    `Category ${
                        category.id || '(unnamed)'
                    } references a missing condition field`
                );
        });
    });
    (config?.rules || []).forEach((rule) =>
        conditionFieldIds(rule.conditions).forEach((fieldId) => {
            if (!ids.has(fieldId))
                errors.push(
                    `Rule ${rule.id || '(unnamed)'} references a missing field`
                );
        })
    );
    (config?.customQuoteConditions || []).forEach((conditionGroup) =>
        conditionFieldIds(conditionGroup).forEach((fieldId) => {
            if (!ids.has(fieldId))
                errors.push(
                    'Custom quote condition references a missing field'
                );
        })
    );
    if (
        !['exact', 'range', 'starting', 'custom-quote'].includes(
            config?.result?.mode
        )
    )
        errors.push(
            'Result mode must be exact, range, starting, or custom-quote'
        );
    return errors;
}

export function calculateEstimate(config, answers = {}) {
    const errors = validateCalculatorConfig(config);
    if (errors.length)
        return { status: 'invalid', errors, total: 0, categories: [] };
    const visibleFieldIds = new Set(
        getVisibleFields(config, answers).map((field) => field.id)
    );
    const categories = (config.categories || [])
        .filter(
            (category) =>
                !category.conditions ||
                evaluateCondition(category.conditions, answers)
        )
        .map((category) => ({
            ...category,
            subtotal: categorySubtotal(category, answers, visibleFieldIds),
        }));
    let adjustments = 0;
    let minimumFloor = 0;
    for (const rule of [...(config.rules || [])].sort(
        (a, b) => number(a.priority) - number(b.priority)
    )) {
        if (!evaluateCondition(rule.conditions, answers)) continue;
        for (const action of rule.actions || []) {
            const target = categories.find(
                (category) => category.id === action.categoryId
            );
            if (action.type === 'fixed') adjustments += number(action.amount);
            if (action.type === 'per-unit')
                adjustments +=
                    number(action.amount) * number(answers[action.fieldId]);
            if (action.type === 'multiplier') {
                if (target)
                    target.subtotal = round(
                        target.subtotal * number(action.factor, 1)
                    );
                else
                    adjustments = round(adjustments * number(action.factor, 1));
            }
            if (action.type === 'percentage') {
                const base = target ? target.subtotal : adjustments;
                const delta = round((base * number(action.percent)) / 100);
                if (target) target.subtotal = round(base + delta);
                else adjustments += delta;
            }
            if (action.type === 'minimum') {
                const minimumTarget =
                    target || minimumCategory(rule, categories);
                if (minimumTarget)
                    minimumTarget.subtotal = Math.max(
                        minimumTarget.subtotal,
                        number(action.amount)
                    );
                else
                    minimumFloor = Math.max(
                        minimumFloor,
                        number(action.amount)
                    );
            }
        }
    }
    const customQuote = (config.customQuoteConditions || []).some(
        (condition) => {
            const fieldIds = conditionFieldIds(condition);
            return (
                fieldIds.length > 0 &&
                fieldIds.every((fieldId) => visibleFieldIds.has(fieldId)) &&
                evaluateCondition(condition, answers)
            );
        }
    );
    const calculatedTotal = round(
        categories.reduce((sum, category) => sum + category.subtotal, 0) +
            adjustments
    );
    const minimumJobPrice = number(config.minimumJobPrice);
    const total = round(
        Math.max(calculatedTotal, minimumJobPrice, minimumFloor)
    );
    return {
        status: customQuote ? 'custom-quote' : 'ready',
        mode: customQuote ? 'custom-quote' : config.result.mode,
        total,
        calculatedTotal,
        minimumApplied:
            minimumJobPrice > 0 && calculatedTotal < minimumJobPrice,
        low: number(config.result.range?.low),
        high: number(config.result.range?.high),
        categories,
        errors: [],
    };
}

export function createEstimatePayload(config, answers, estimate) {
    return {
        calculatorId: config?.id || '',
        calculatorName: config?.name || '',
        answers: structuredClone(answers || {}),
        estimate: structuredClone(estimate || {}),
        summary:
            estimate?.status === 'custom-quote'
                ? 'Custom quote requested'
                : `${estimate?.mode || 'exact'} estimate: ${
                      estimate?.total ?? 0
                  }`,
    };
}

export const SAMPLE_CALCULATORS = {
    windowCleaning: {
        schemaVersion: 1,
        id: 'window-cleaning',
        name: 'Residential window cleaning',
        currency: 'USD',
        minimumJobPrice: 150,
        content: {
            tagline: 'Instant estimate',
            heading: 'Get an instant window cleaning estimate',
            description:
                'Answer a few questions to see an estimated price for your home.',
            resultLabel: 'Estimated total',
            disclaimer: 'Final pricing may vary after an on-site review.',
        },
        fields: [
            {
                id: 'windows',
                type: 'quantity',
                label: 'How many windows?',
                required: true,
                min: 1,
                step: 1,
                defaultValue: 20,
            },
            {
                id: 'serviceCategory',
                type: 'select',
                label: 'What service are you looking for?',
                options: [
                    { value: 'window-cleaning', label: 'Window cleaning' },
                ],
                defaultValue: 'window-cleaning',
            },
            {
                id: 'service',
                type: 'select',
                label: 'Which window cleaning do you need?',
                options: [
                    { value: 'interior', label: 'Interior only' },
                    { value: 'exterior', label: 'Exterior only' },
                    { value: 'inside-out', label: 'Interior and exterior' },
                ],
                defaultValue: 'inside-out',
            },
            {
                id: 'stories',
                type: 'select',
                label: 'How many stories?',
                options: [
                    { value: '1', label: 'One story' },
                    { value: '2', label: 'Two or more stories' },
                ],
                defaultValue: '2',
            },
            {
                id: 'screens',
                type: 'quantity',
                label: 'How many screens?',
                min: 0,
                step: 1,
                defaultValue: 10,
            },
        ],
        categories: [
            {
                id: 'windows',
                name: 'Window cleaning',
                quantityFieldId: 'windows',
                items: [
                    {
                        fieldId: 'service',
                        options: [
                            {
                                value: 'interior',
                                price: 8,
                                pricingType: 'per-unit',
                            },
                            {
                                value: 'exterior',
                                price: 8,
                                pricingType: 'per-unit',
                            },
                            {
                                value: 'inside-out',
                                price: 12,
                                pricingType: 'per-unit',
                            },
                        ],
                    },
                ],
            },
            {
                id: 'screens',
                name: 'Screens',
                items: [
                    { fieldId: 'screens', price: 3, pricingType: 'per-unit' },
                ],
            },
        ],
        rules: [
            {
                id: 'stories',
                name: 'Second-story adjustment',
                priority: 10,
                conditions: {
                    conditions: [
                        { fieldId: 'stories', operator: 'equals', value: '2' },
                    ],
                },
                actions: [
                    { type: 'multiplier', categoryId: 'windows', factor: 1.2 },
                ],
            },
        ],
        customQuoteConditions: [
            {
                conditions: [
                    { fieldId: 'windows', operator: 'greater-than', value: 40 },
                ],
            },
        ],
        result: { mode: 'exact' },
        actions: {
            primaryText: 'Book your cleaning',
            primaryLink: '',
            primaryIcon: '',
            primaryIconVisible: true,
            primaryOpenInNewTab: false,
            secondaryText: 'Call us',
            secondaryLink: '',
            secondaryIcon: '',
            secondaryIconVisible: true,
            secondaryOpenInNewTab: false,
        },
        leadCapture: {
            sdkPublicKey: '',
            eyebrow: 'Next step',
            title: "Let's talk about your estimate",
            description:
                "Share your contact details and we'll follow up about your project and estimate.",
            submitText: 'Request a follow-up',
            successTitle: 'Request received',
            successMessage: 'Thanks! Your estimate request has been sent.',
            fields: structuredClone(DEFAULT_LEAD_CAPTURE_FIELDS),
            photoUpload: structuredClone(DEFAULT_LEAD_PHOTO_UPLOAD),
            terms: {
                enabled: false,
                required: true,
                message:
                    'I agree that this business may collect and use my information to contact me about this estimate.',
            },
        },
    },
    pressureWashing: {
        schemaVersion: 1,
        id: 'house-washing',
        name: 'House washing estimate',
        currency: 'USD',
        minimumJobPrice: 199,
        content: {
            heading: 'Estimate your house washing project',
            resultLabel: 'Estimated total',
        },
        fields: [
            {
                id: 'sqft',
                type: 'square-footage',
                label: 'Approximate square footage',
                defaultValue: 1200,
            },
        ],
        categories: [
            {
                id: 'wash',
                name: 'House washing',
                quantityFieldId: 'sqft',
                items: [
                    { fieldId: 'sqft', price: 0.25, pricingType: 'per-unit' },
                ],
            },
        ],
        rules: [],
        customQuoteConditions: [],
        result: { mode: 'exact' },
        actions: {
            primaryText: 'Request a quote',
            primaryLink: '',
        },
        leadCapture: {
            sdkPublicKey: '',
            fields: structuredClone(DEFAULT_LEAD_CAPTURE_FIELDS),
            photoUpload: structuredClone(DEFAULT_LEAD_PHOTO_UPLOAD),
            terms: { enabled: false, required: true, message: '' },
        },
    },
    renovation: {
        schemaVersion: 1,
        id: 'renovation-estimate',
        name: 'Renovation estimate',
        currency: 'USD',
        content: {
            heading: 'Get a renovation budget range',
            resultLabel: 'Estimated range',
        },
        fields: [
            {
                id: 'projectType',
                type: 'select',
                label: 'What are you renovating?',
                options: [
                    { value: 'kitchen', label: 'Kitchen' },
                    { value: 'bathroom', label: 'Bathroom' },
                ],
                defaultValue: 'kitchen',
            },
        ],
        categories: [],
        rules: [],
        customQuoteConditions: [],
        result: { mode: 'range', range: { low: 8000, high: 15000 } },
        actions: {
            primaryText: 'Discuss your project',
            primaryLink: '',
        },
        leadCapture: {
            sdkPublicKey: '',
            fields: structuredClone(DEFAULT_LEAD_CAPTURE_FIELDS),
            photoUpload: structuredClone(DEFAULT_LEAD_PHOTO_UPLOAD),
            terms: { enabled: false, required: true, message: '' },
        },
    },
    roofing: {
        schemaVersion: 1,
        id: 'roofing-estimate',
        name: 'Residential roofing estimate',
        industry: 'Roofing',
        service: 'Roof replacement',
        currency: 'USD',
        minimumJobPrice: 2500,
        content: {
            tagline: 'Instant estimate',
            heading: 'Estimate your roofing project',
            description:
                'Choose your roof type and answer a few relevant questions for an estimated project price.',
            resultLabel: 'Estimated total',
            disclaimer:
                'Final pricing depends on an on-site roof inspection and material availability.',
            customQuoteMessage:
                'This roof needs an in-person assessment. Request a detailed quote.',
        },
        fields: [
            {
                id: 'roofType',
                type: 'select',
                label: 'What type of roof do you have?',
                required: true,
                options: [
                    { value: 'shingle', label: 'Shingle roof' },
                    { value: 'flat', label: 'Flat roof' },
                    { value: 'metal', label: 'Metal roof' },
                ],
                defaultValue: 'shingle',
            },
            {
                id: 'roofArea',
                type: 'square-footage',
                label: 'Approximate roof area (sq. ft.)',
                required: true,
                min: 250,
                step: 50,
                defaultValue: 2000,
            },
            {
                id: 'shingleGrade',
                type: 'select',
                label: 'Which shingle grade would you like?',
                options: [
                    { value: 'architectural', label: 'Architectural shingle' },
                    { value: 'premium', label: 'Premium designer shingle' },
                ],
                defaultValue: 'architectural',
                visibility: {
                    conditions: [
                        {
                            fieldId: 'roofType',
                            operator: 'equals',
                            value: 'shingle',
                        },
                    ],
                },
            },
            {
                id: 'removeExistingShingles',
                type: 'boolean',
                label: 'Do existing shingles need to be removed?',
                defaultValue: false,
                visibility: {
                    conditions: [
                        {
                            fieldId: 'roofType',
                            operator: 'equals',
                            value: 'shingle',
                        },
                    ],
                },
            },
            {
                id: 'flatMembrane',
                type: 'select',
                label: 'Which flat-roof membrane would you like?',
                options: [
                    { value: 'tpo', label: 'TPO membrane' },
                    { value: 'epdm', label: 'EPDM rubber membrane' },
                ],
                defaultValue: 'tpo',
                visibility: {
                    conditions: [
                        {
                            fieldId: 'roofType',
                            operator: 'equals',
                            value: 'flat',
                        },
                    ],
                },
            },
            {
                id: 'drainageAreas',
                type: 'quantity',
                label: 'How many drainage areas need work?',
                min: 0,
                step: 1,
                defaultValue: 2,
                visibility: {
                    conditions: [
                        {
                            fieldId: 'roofType',
                            operator: 'equals',
                            value: 'flat',
                        },
                    ],
                },
            },
            {
                id: 'metalStyle',
                type: 'select',
                label: 'Which metal roof style do you have?',
                options: [
                    { value: 'standing-seam', label: 'Standing seam' },
                    { value: 'corrugated', label: 'Corrugated panel' },
                ],
                defaultValue: 'standing-seam',
                visibility: {
                    conditions: [
                        {
                            fieldId: 'roofType',
                            operator: 'equals',
                            value: 'metal',
                        },
                    ],
                },
            },
            {
                id: 'rustRepair',
                type: 'boolean',
                label: 'Does the roof need rust repair?',
                defaultValue: false,
                visibility: {
                    conditions: [
                        {
                            fieldId: 'roofType',
                            operator: 'equals',
                            value: 'metal',
                        },
                    ],
                },
            },
        ],
        categories: [
            {
                id: 'shingle-roof',
                name: 'Shingle roof replacement',
                quantityFieldId: 'roofArea',
                conditions: {
                    conditions: [
                        {
                            fieldId: 'roofType',
                            operator: 'equals',
                            value: 'shingle',
                        },
                    ],
                },
                items: [
                    {
                        fieldId: 'shingleGrade',
                        options: [
                            {
                                value: 'architectural',
                                price: 6,
                                pricingType: 'per-unit',
                            },
                            {
                                value: 'premium',
                                price: 8,
                                pricingType: 'per-unit',
                            },
                        ],
                    },
                ],
            },
            {
                id: 'shingle-removal',
                name: 'Existing shingle removal',
                quantityFieldId: 'roofArea',
                conditions: {
                    logic: 'and',
                    conditions: [
                        {
                            fieldId: 'roofType',
                            operator: 'equals',
                            value: 'shingle',
                        },
                        {
                            fieldId: 'removeExistingShingles',
                            operator: 'equals',
                            value: true,
                        },
                    ],
                },
                items: [
                    {
                        fieldId: 'roofArea',
                        price: 1.5,
                        pricingType: 'per-unit',
                    },
                ],
            },
            {
                id: 'flat-roof',
                name: 'Flat roof replacement',
                quantityFieldId: 'roofArea',
                conditions: {
                    conditions: [
                        {
                            fieldId: 'roofType',
                            operator: 'equals',
                            value: 'flat',
                        },
                    ],
                },
                items: [
                    {
                        fieldId: 'flatMembrane',
                        options: [
                            { value: 'tpo', price: 7, pricingType: 'per-unit' },
                            {
                                value: 'epdm',
                                price: 8,
                                pricingType: 'per-unit',
                            },
                        ],
                    },
                ],
            },
            {
                id: 'flat-drainage',
                name: 'Drainage work',
                conditions: {
                    conditions: [
                        {
                            fieldId: 'roofType',
                            operator: 'equals',
                            value: 'flat',
                        },
                    ],
                },
                items: [
                    {
                        fieldId: 'drainageAreas',
                        price: 250,
                        pricingType: 'per-unit',
                    },
                ],
            },
            {
                id: 'metal-roof',
                name: 'Metal roof replacement',
                quantityFieldId: 'roofArea',
                conditions: {
                    conditions: [
                        {
                            fieldId: 'roofType',
                            operator: 'equals',
                            value: 'metal',
                        },
                    ],
                },
                items: [
                    {
                        fieldId: 'metalStyle',
                        options: [
                            {
                                value: 'standing-seam',
                                price: 11,
                                pricingType: 'per-unit',
                            },
                            {
                                value: 'corrugated',
                                price: 9,
                                pricingType: 'per-unit',
                            },
                        ],
                    },
                ],
            },
            {
                id: 'metal-rust-repair',
                name: 'Rust repair allowance',
                conditions: {
                    logic: 'and',
                    conditions: [
                        {
                            fieldId: 'roofType',
                            operator: 'equals',
                            value: 'metal',
                        },
                        {
                            fieldId: 'rustRepair',
                            operator: 'equals',
                            value: true,
                        },
                    ],
                },
                items: [
                    { fieldId: 'rustRepair', price: 800, pricingType: 'fixed' },
                ],
            },
        ],
        rules: [],
        customQuoteConditions: [
            {
                conditions: [
                    {
                        fieldId: 'roofArea',
                        operator: 'greater-than',
                        value: 5000,
                    },
                ],
            },
        ],
        result: { mode: 'exact' },
        actions: {
            primaryText: 'Request a roofing quote',
            primaryLink: '',
            primaryIcon: '',
            primaryIconVisible: true,
            primaryOpenInNewTab: false,
            secondaryText: 'Call us',
            secondaryLink: '',
            secondaryIcon: '',
            secondaryIconVisible: true,
            secondaryOpenInNewTab: false,
        },
        leadCapture: {
            sdkPublicKey: '',
            fields: structuredClone(DEFAULT_LEAD_CAPTURE_FIELDS),
            photoUpload: structuredClone(DEFAULT_LEAD_PHOTO_UPLOAD),
            terms: { enabled: false, required: true, message: '' },
        },
    },
};

function cloneTemplate(config, id, name, industry, service) {
    return {
        ...structuredClone(config),
        id,
        name,
        industry,
        service,
    };
}

export const CALCULATOR_TEMPLATES = [
    {
        id: 'general-service',
        label: 'General service estimate',
        industry: 'General',
        config: cloneTemplate(
            SAMPLE_CALCULATORS.windowCleaning,
            'general-service',
            'Service estimate',
            'General',
            'Service'
        ),
    },
    {
        id: 'window-cleaning',
        label: 'Residential window cleaning',
        industry: 'Window cleaning',
        config: SAMPLE_CALCULATORS.windowCleaning,
    },
    {
        id: 'house-washing',
        label: 'House washing',
        industry: 'Pressure washing',
        config: SAMPLE_CALCULATORS.pressureWashing,
    },
    {
        id: 'driveway-washing',
        label: 'Driveway pressure washing',
        industry: 'Pressure washing',
        config: cloneTemplate(
            SAMPLE_CALCULATORS.pressureWashing,
            'driveway-pressure-washing',
            'Driveway pressure washing estimate',
            'Pressure washing',
            'Driveway washing'
        ),
    },
    {
        id: 'interior-painting',
        label: 'Interior painting',
        industry: 'Painting',
        config: cloneTemplate(
            SAMPLE_CALCULATORS.renovation,
            'interior-painting',
            'Interior painting estimate',
            'Painting',
            'Interior painting'
        ),
    },
    {
        id: 'landscaping',
        label: 'Landscaping',
        industry: 'Landscaping',
        config: cloneTemplate(
            SAMPLE_CALCULATORS.pressureWashing,
            'landscaping',
            'Landscaping estimate',
            'Landscaping',
            'Landscaping'
        ),
    },
    {
        id: 'plumbing',
        label: 'Plumbing service call',
        industry: 'Plumbing',
        config: cloneTemplate(
            SAMPLE_CALCULATORS.windowCleaning,
            'plumbing-service-call',
            'Plumbing service call estimate',
            'Plumbing',
            'Service call'
        ),
    },
    {
        id: 'hvac',
        label: 'HVAC maintenance',
        industry: 'HVAC',
        config: cloneTemplate(
            SAMPLE_CALCULATORS.windowCleaning,
            'hvac-maintenance',
            'HVAC maintenance estimate',
            'HVAC',
            'Maintenance'
        ),
    },
    {
        id: 'roofing',
        label: 'Roof replacement',
        industry: 'Roofing',
        config: SAMPLE_CALCULATORS.roofing,
    },
    {
        id: 'renovation',
        label: 'Renovation estimate',
        industry: 'Renovations',
        config: SAMPLE_CALCULATORS.renovation,
    },
];
